import Badge from "@/components/core/badge";
import { Blanket, BlanketImage, BlanketInfo } from "@/components/core/blanket";
import { Button } from "@/components/core/buttons/button";
import DataTable, { type ColumnDef } from "@/components/core/data-table";
import Pagination from "@/components/core/pagination";
import { Searchbar } from "@/components/core/searchbar";
import { Content } from "@/components/layouts/content";
import { userPermissions } from "@/constants/user-permissions";
import { caseRecordsQueryOptions } from "@/features/case-record/api/queries/case-records-query";
import type { CaseRecord } from "@/features/case-record/types/case-records";
import { HasPermission } from "@/hooks/use-auth";
import { formatDate } from "@/utils/format-date";
import { IconMoodLookDown, IconPlus } from "@tabler/icons-react";
import { useSuspenseQuery } from "@tanstack/react-query";
import { Link, createFileRoute, useNavigate } from "@tanstack/react-router";
import { z } from "zod";

const columns: ColumnDef<CaseRecord>[] = [
	{
		id: "case_number",
		label: "Case No. / Case Title",
		cellClassName: "min-w-[150px] text-left",
		headerClassName: "whitespace-nowrap",
		cell: ({ data }) => (
			<Link to={`/case-filings/${data.id}`} className="flex flex-col font-semibold">
				<span className="text-blue-600">{data.case_number}</span>
				<span className="text-foreground-dark">{data.case_title}</span>
			</Link>
		),
	},
	{
		id: "case_category",
		label: "Case Category",
		cellClassName: "",
		headerClassName: "whitespace-nowrap",
		cell: ({ data }) => <span>{data.case_category}</span>,
	},
	{
		id: "case_type",
		label: "Case Type",
		cellClassName: "",
		headerClassName: "whitespace-nowrap",
		cell: ({ data }) => <span>{data.case_type}</span>,
	},
	{
		id: "filed_at",
		label: "Date Filed",
		cellClassName: "text-right whitespace-nowrap",
		headerClassName: "whitespace-nowrap text-right",
		cell: ({ data }) => formatDate(data.filed_at, "PP"),
	},
];

export const Route = createFileRoute("/_backoffice/case-filings_/")({
	validateSearch: z.object({
		search: z.string().optional(),
		per_page: z.number().optional(),
		page: z.number().nullable().optional(),
	}),
	loaderDeps: ({ search }) => ({
		page: search.page,
		per_page: search.per_page,
		search: search.search,
		status: "draft",
	}),
	loader: ({ context: { queryClient }, deps }) => {
		return queryClient.ensureQueryData(
			caseRecordsQueryOptions({
				...deps,
			}),
		);
	},
	component: RouteComponent,
});

function RouteComponent() {
	const navigate = useNavigate({
		from: "/case-filings",
	});
	const search = Route.useSearch();
	const caseQuery = useSuspenseQuery(
		caseRecordsQueryOptions({
			...search,
			status: "draft",
		}),
	);

	const cases = caseQuery.data.data;
	const meta = caseQuery.data.meta;

	return (
		<Content pageName="Case Filings">
			<div className="flex flex-col w-full max-w-base p-6 gap-6">
				<div className="flex flex-row items-center justify-between w-full gap-4">
					<div className="inline-flex flex-col w-auto">
						<span className="text-xl text-foreground-dark font-semibold">Draft Case Filings</span>
						<span className="text-base text-foreground/70">Review and manage draft case submissions</span>
					</div>
					<div className="flex gap-2">
						<HasPermission permissions={[userPermissions.ADD_CASE_FILING]}>
							<Link to="/case-filings/create">
								<Button>
									<IconPlus size={18} stroke={2} />
									<span>New Case Filing</span>
								</Button>
							</Link>
						</HasPermission>
					</div>
				</div>

				<div className="flex flex-col w-full gap-4">
					<Searchbar
						defaultValue={search.search}
						onChange={async (value) => {
							await navigate({
								to: "/case-filings",
								search: (old) => ({
									...old,
									search: value,
									page: 1,
								}),
							});
						}}
					/>
					{cases.length > 0 ? (
						<>
							<DataTable
								className="border-collapse"
								data={cases}
								columns={columns}
								stickyHead={false}
								stickyCol={false}
								showBorder={true}
								compact={true}
								showToolbar={false}
							/>
							<Pagination
								onClick={async (page) => {
									await navigate({
										to: "/case-filings",
										search: (old) => ({
											...old,
											page,
										}),
									});
								}}
								initialPage={meta.current_page}
								pageCount={meta.last_page}
								itemsPerPage={search.per_page || 10}
								onItemsPerPageChange={async (value) => {
									await navigate({
										to: "/case-filings",
										search: (old) => ({
											...old,
											per_page: value,
											page: 1,
										}),
									});
								}}
								itemsPerPageOptions={[10, 20, 50, 100]}
							/>
						</>
					) : (
						<Blanket width="sm">
							<BlanketImage icon={<IconMoodLookDown size={30} stroke={1.5} />} />
							<BlanketInfo
								title="No Draft Filings"
								message="No draft case filings found. All cases have been processed or create a new filing to get started."
							/>
						</Blanket>
					)}
				</div>
			</div>
		</Content>
	);
}
